import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { OrderResMock } from '../types/order.type'

type OrderTypes = {
  patientId?: string
  id?: string
  captured_date?: string
  status?: 'pending' | 'completed' | 'canceled' | 'in_progress'
  limit?: string
  page?: string
  sortDir?: string
  query?: string
  orderable_type?: 'LabTest' | 'Imaging'
  encounterId?: string
}
export const useOrder = (arg?: OrderTypes) => {
  const patientId = arg?.patientId
  const id = arg?.id
  const captured_date = arg?.captured_date
  const status = arg?.status
  const limit = arg?.limit
  const page = arg?.page
  const sortDir = arg?.sortDir || 'desc'
  const query = arg?.query
  const orderableType = arg?.orderable_type || 'LabTest'
  const encounterId = arg?.encounterId

  const { data: orders, isLoading: ordersIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<OrderResMock>(`/orders`, {
        params: {
          ...(captured_date && { order_date: captured_date }),
          ...(encounterId && { encounter_id: encounterId }),
          ...(orderableType && { orderable_type: orderableType }),
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(patientId && { patient_id: patientId }),
          ...(query && { query }),
          ...(sortDir && { sort_dir: sortDir }),
          ...(status && { status })
        }
      })
      return data
    },
    queryKey: ['orders', { captured_date, encounterId, limit, orderableType, page, patientId, query, sortDir, status }]
  })

  const { data: order, isLoading: orderIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<OrderResMock>(`/orders/${id}`)
      return data
    },
    enabled: !!id,
    queryKey: ['orders', { id }]
  })

  const { mutateAsync: createOrder } = useMutation({
    mutationFn: async (formVal: any) => {
      let endpoint = `/orders`
      const config = { headers: { 'Content-Type': 'application/vnd.api+json' } }
      const payload = formVal
      if (patientId) {
        endpoint += `?patient_id=${patientId}`
      }
      const { data } = await AXIOS_INSTANCE.post(endpoint, payload, config)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['consent_forms'] })
    }
  })

  const { mutateAsync: updateOrder } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`orders/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['orders', { id }] })
      queryClient.invalidateQueries({ queryKey: ['consent_forms'] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  const { mutateAsync: updateOrderStatus } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      await AXIOS_INSTANCE.patch(`orders/${id}/update`, formVal, {
        headers: { 'Content-Type': 'application/json' }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', { id }] })
      queryClient.invalidateQueries({ queryKey: ['consent_forms'] })
    }
  })

  const { mutateAsync: deleteOrder } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`orders/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orders', { id }] })
  })

  return {
    createOrder,
    deleteOrder,
    included: orders?.included,
    lab_orders: orders?.data,
    orders,
    ordersIsLoading,
    updateOrder,
    updateOrderStatus,
    order
  }
}
